<script lang="ts">
	// Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';

	// Services and configs
	import { devices_config } from 'src/configs/devices';
	import { getDeviceChartsConfig } from 'src/helpers/statistics/config/device_chart_config';

	// Constants
	import { MOTION_SENSOR_TYPES, GAS_SENSOR_TYPES } from 'src/constants';

	// Helpers
	import { checkIsCriticalEvent } from 'src/helpers/device_data_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		createGen1StatusLabels,
		createGen1CombinedLabelList,
		extractGen1MainLabel,
		getGen1DeviceStatuses,
		canShowGen1LabelList,
	} from 'src/components/Sidepanel/SidePanelLayoutProvider/SidePanelDeviceLayout/Devices/Shelly/Gen1/gen1-main-helpers';

	// Design System Components
	import { ParameterCardV2 } from '@Shelly/shelly-design-system';
	// Helpers
	import { mapIconToThin } from 'src/helpers/new-layout-helpers';

	// Components
	import ThumbLayerChart from 'src/components/Chart/LayerChart/ThumbLayerChart.svelte';
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import ControlGasSensorMute from 'src/components/Controls/Single/ControlGasSensorMute.svelte';
	import LastEvents from 'src/components/EventLog/LastEvents.svelte';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceLabelList from 'src/components/Sidepanel/DeviceViews/DeviceLabelList.svelte';

	import { useNavigateToHistoryStatisticsTab } from 'src/components/Sidepanel/useNavigateToHistoryStatisticsTab';
	import { useNavigateToHistoryTab } from 'src/components/Sidepanel/useNavigateToHistoryTab';

	// Types
	import type { Device } from 'src/types/Device.ts';
	/**
	 * Main control tab for the sensor devices sidepanel drawer.
	 *
	 * Responsibilities:
	 * - displays label list with sensor statuses (temperature, battery)
	 * - shows device charts when configured
	 * - shows sensor status as ParameterCard
	 * - shows firmware incompatibility warnings
	 *
	 * @param {Device} device - The device for which the information is displayed.
	 */
	export let device: Device;

	const { navigateToHistoryStatisticsTab } = useNavigateToHistoryStatisticsTab();

	// Helper functions for device type checks
	$: isMotionSensor = MOTION_SENSOR_TYPES.includes(
		device.type as (typeof MOTION_SENSOR_TYPES)[number],
	);
	$: isGasSensor = GAS_SENSOR_TYPES.includes(device.type as (typeof GAS_SENSOR_TYPES)[number]);

	$: deviceConfig = devices_config?.[device?.type];

	let timerLabels: Record<string, any> = {};

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;

	$: _statuses = getGen1DeviceStatuses(status, device, settings, deviceConfig);
	$: labelList = createGen1StatusLabels(_statuses, device);

	$: combinedLabelList = createGen1CombinedLabelList(timerLabels, labelList);
	$: mainLabel = extractGen1MainLabel(_statuses);

	$: deviceChartsConfig = device ? getDeviceChartsConfig(device) : [];

	const handleOnClickThumbChart = (event: CustomEvent | Event) => {
		navigateToHistoryStatisticsTab();
	};

	const handleMainLabelClick = () => {
		if (deviceChartsConfig.length) {
			navigateToHistoryStatisticsTab();
		}
	};

	// Get navigation context during component initialization
	const { navigateToHistoryTab } = useNavigateToHistoryTab();

	const handleLastEventsClick = (event: CustomEvent) => {
		if (!device.shared && event.detail?.originalTimestamp) {
			navigateToHistoryTab(event.detail.originalTimestamp);
		}
	};
</script>

<DeviceViewStateWrapper {device}>
	<div class="label-list-wrapper mb-s" slot="staticTop">
		{#if canShowGen1LabelList(device, status)}
			<DeviceLabelList labelList={combinedLabelList} />
		{/if}
		<DeviceNotificationLabel deviceId={device?.id} />
	</div>

	<div class="flex-full-height justify-content-end" slot="staticBottom">
		<div>
			{#if deviceChartsConfig.length}
				<ThumbLayerChart
					class={mainLabel ? 'mb-s' : ''}
					{device}
					on:click={handleOnClickThumbChart}
				/>
			{/if}

			{#if isMotionSensor}
				<div class="flex-full-height justify-content-end">
					<LastEvents {device} on:click={handleLastEventsClick} />
				</div>
			{/if}

			{#if mainLabel}
				{@const isCriticalEvent = checkIsCriticalEvent(mainLabel.value)}
				<ParameterCardV2
					class={deviceChartsConfig.length > 0 ? 'mb-s cursor-pointer' : 'mb-s'}
					icon={mapIconToThin(mainLabel.icon)}
					title={capFirstChar(mainLabel.value)}
					description={capFirstChar(mainLabel.translation)}
					backgroundColor={isCriticalEvent ? '--color-danger-10' : '--color-0'}
					isFocusable={deviceChartsConfig.length > 0}
					on:click={handleMainLabelClick}
				/>
			{/if}
		</div>
		{#if isGasSensor}
			<ControlGasSensorMute {device} />
		{/if}
	</div>
</DeviceViewStateWrapper>
